import { ErrorResponse } from 'react-router-dom';
import {
  IExpeditionCertificateDataSource,
  IExpeditionCertificateInitialValues,
  IResponse,
} from '../types';
import { prepareFormData } from '../utils/functions';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';
import { IExpandedRow } from '../components/DataTable/type';

export default class ExpeditionCertificateService {
  private readonly baseUrl: string =
    'transportation-policies/expedition-certificates/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string,
  ) {}

  async fetchData(): Promise<
    IResponse<IExpeditionCertificateDataSource> | ErrorResponse | null
  > {
    return await this.executeService<
      IResponse<IExpeditionCertificateDataSource>
    >({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IExpeditionCertificateDataSource | ErrorResponse | null> {
    return await this.executeService<IExpeditionCertificateDataSource>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService<{ download_url: string }>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async submitForm(
    values: IExpeditionCertificateInitialValues,
    selectedItem?: IExpeditionCertificateDataSource,
  ): Promise<IExpeditionCertificateDataSource | ErrorResponse | null> {
    const formData =
      prepareFormData<IExpeditionCertificateInitialValues>(values);

    const method = selectedItem ? RequestMethods.PUT : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IExpeditionCertificateDataSource>({
      method,
      url,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async updateStatus(
    id: number,
    status: string,
    email_users: number[],
  ): Promise<IExpeditionCertificateDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('email_users', email_users.join(','));

    return await this.executeService<IExpeditionCertificateDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IExpeditionCertificateDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('upload_files', file);
    });

    return await this.executeService<IExpeditionCertificateDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IExpeditionCertificateDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService<IExpeditionCertificateDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async fetchExpandedRow(
    id: number,
  ): Promise<IExpandedRow[] | ErrorResponse | null> {
    return await this.executeService<IExpandedRow[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/get-latest-processes/`,
    });
  }
}
